package one.healthium.uscreen.ui.blocks

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.tween
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import kotlinx.coroutines.delay
import one.healthium.uscreen.ui.animation.POST_TRANSITION_DELAY
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEachIndexed
import androidx.compose.ui.util.fastRoundToInt
import one.healthium.base.utils.BodyParamsUtils
import one.healthium.domain.common.BMIRange
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.ui.compose.theme.black25
import one.healthium.ui.compose.theme.black45
import one.healthium.ui.compose.theme.gray_10
import one.healthium.ui.compose.theme.gray_11
import one.healthium.ui.compose.theme.gray_9
import one.healthium.ui.compose.theme.white
import one.healthium.ui.compose.theme.yellow_3
import one.healthium.uscreen.domain.models.BmiCardBlock
import one.healthium.uscreen.domain.models.BmiCategory
import one.healthium.uscreen.ui.UScreenAnswer
import one.healthium.uscreen.ui.util.uscreenPadding
import kotlin.math.max
import kotlin.math.min

private val cardShape = RoundedCornerShape(size = 12.dp)
private val bmiDotSize = 28.dp
private val bmiGraphBarHeight = 8.dp

@Composable
fun UScreenBmiCardBlock(
    modifier: Modifier,
    block: BmiCardBlock,
    answers: Map<String, UScreenAnswer>,
) {
    val weightAnswer = answers[block.weightKey] as? UScreenAnswer.NumberAnswer ?: return
    val heightAnswer = answers[block.heightKey] as? UScreenAnswer.NumberAnswer ?: return

    val weight = weightAnswer.value
    val height = heightAnswer.value

    val bmi = BodyParamsUtils.bmi(weight, height) ?: return
    val bmiRange = BMIRange.byValue(bmi)

    val categoryKey = when (bmiRange) {
        BMIRange.Underweight -> "underweight"
        BMIRange.Normal -> "normal"
        BMIRange.Overweight -> "overweight"
        BMIRange.Obese -> "obese"
    }
    val category = block.categories[categoryKey]

    Column(
        modifier = modifier
            .fillMaxWidth()
            .uscreenPadding(block.padding)
            .background(gray_9, cardShape)
            .padding(12.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        Text(
            text = "Body Mass Index (BMI)",
            style = AppTheme.textStyles.captionBold,
            color = gray_10,
        )
        Spacer(Modifier.height(10.dp))
        BmiBarGraph(bmi, bmiRange)
        Spacer(Modifier.height(20.dp))
        if (category != null) {
            BmiCategoryInfo(category)
        }
    }
}

@Composable
private fun BmiBarGraph(currentBmi: Int, currentRange: BMIRange) {
    val parentSize = remember { mutableIntStateOf(0) }
    val density = LocalDensity.current
    val animationProgress = remember { Animatable(0f) }

    LaunchedEffect(Unit) {
        delay(POST_TRANSITION_DELAY + 100)
        animationProgress.animateTo(1f, tween(800))
    }

    Box(
        modifier = Modifier
            .fillMaxWidth()
            .onGloballyPositioned {
                parentSize.intValue = it.size.width
            },
        contentAlignment = Alignment.BottomStart,
    ) {
        val availableSize = parentSize.intValue.toFloat() - with(density) { bmiDotSize.toPx() }
        val part = availableSize / 10

        val targetOffset = when (currentBmi) {
            in 0..10 -> 0f
            in 10..20 -> ((currentBmi - 10) / 10f) * (part * 4)
            in 21..26 -> ((currentBmi - 20) / 6f) * (part * 3) + part * 4
            in 27..32 -> ((currentBmi - 26) / 6f) * (part * 3) + part * 4 + part * 3
            else -> availableSize
        }
        val dotOffset = targetOffset * animationProgress.value

        GradientBar(currentRange)
        BmiDot(
            Modifier
                .offset {
                    IntOffset(
                        x = dotOffset.fastRoundToInt(),
                        y = 0,
                    )
                }
                .padding(bottom = 12.dp)
        )
        BmiTooltip(
            modifier = Modifier.padding(bottom = 56.dp),
            dotOffset = dotOffset,
            availableSpace = parentSize.intValue,
            currentBmi = currentBmi,
        )
    }
}

@Composable
private fun BmiDot(modifier: Modifier = Modifier) {
    Box(
        modifier = modifier,
        contentAlignment = Alignment.Center,
    ) {
        Box(
            Modifier
                .size(bmiDotSize)
                .background(gray_11, RoundedCornerShape(100))
        )
        Box(
            Modifier
                .size(12.dp)
                .background(white, RoundedCornerShape(100))
        )
    }
}

@Composable
private fun GradientBar(currentRange: BMIRange) {
    Column(
        Modifier.padding(horizontal = (bmiDotSize - bmiGraphBarHeight) / 2)
    ) {
        Row {
            BMIRange.entries.fastForEachIndexed { index, bmiRange ->
                if (index > 0) {
                    when (bmiRange) {
                        BMIRange.Underweight -> {}
                        BMIRange.Normal -> Spacer(Modifier.weight(3.9f))
                        BMIRange.Overweight -> Spacer(Modifier.weight(2.9f))
                        BMIRange.Obese -> Spacer(Modifier.weight(3f))
                    }
                }
                Text(
                    text = when (bmiRange) {
                        BMIRange.Underweight -> "10"
                        BMIRange.Normal -> "20"
                        BMIRange.Overweight -> "26"
                        BMIRange.Obese -> "32"
                    },
                    style = AppTheme.textStyles.default10Medium,
                    color = black25,
                )
            }
        }
        Spacer(Modifier.height(8.dp))
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .height(bmiGraphBarHeight)
                .background(
                    Brush.linearGradient(
                        0f to Color(0xFF00BDFF),
                        0.28f to Color(0xFF00DB58),
                        0.56f to Color(0xFFFFD704),
                        0.81f to Color(0xFFFFB800),
                        1f to Color(0xFFDA4960),
                    ),
                    RoundedCornerShape(100),
                ),
        )
        Spacer(Modifier.height(10.dp))
        Row {
            BMIRange.entries.fastForEachIndexed { index, bmiRange ->
                if (index > 0) {
                    Spacer(Modifier.weight(1f))
                }
                Text(
                    text = when (bmiRange) {
                        BMIRange.Underweight -> "Underweight"
                        BMIRange.Normal -> "Normal"
                        BMIRange.Overweight -> "Overweight"
                        BMIRange.Obese -> "Obese"
                    },
                    style = if (currentRange == bmiRange) {
                        AppTheme.textStyles.default10ExtraBold
                    } else {
                        AppTheme.textStyles.default10Medium
                    },
                    color = if (currentRange == bmiRange) gray_10 else black45,
                )
            }
        }
    }
}

@Composable
private fun BmiTooltip(
    modifier: Modifier = Modifier,
    dotOffset: Float,
    availableSpace: Int,
    currentBmi: Int,
) {
    val tooltipSize = remember { mutableStateOf(IntSize.Zero) }
    Box(modifier) {
        Box(
            modifier = Modifier
                .onGloballyPositioned {
                    tooltipSize.value = it.size
                }
                .offset {
                    val availableSpaceTooltip = availableSpace - tooltipSize.value.width
                    val tooltipOffset =
                        dotOffset + bmiDotSize.div(2).toPx() - tooltipSize.value.width.div(2)
                    IntOffset(
                        x = min(
                            availableSpaceTooltip,
                            max(0, tooltipOffset.fastRoundToInt()),
                        ),
                        y = 0,
                    )
                }
                .drawBehind {
                    drawRoundRect(
                        color = gray_10,
                        cornerRadius = CornerRadius(x = 6.dp.toPx(), y = 6.dp.toPx()),
                    )
                }
                .padding(6.dp),
        ) {
            Text(
                text = "BMI $currentBmi",
                style = AppTheme.textStyles.captionBold,
                color = white,
            )
        }
        Box(
            modifier = Modifier
                .offset {
                    IntOffset(
                        x = (dotOffset + bmiDotSize.div(2).toPx()).fastRoundToInt(),
                        y = tooltipSize.value.height - 2,
                    )
                }
                .drawBehind {
                    drawTooltipTriangle(gray_10)
                },
        )
    }
}

private fun DrawScope.drawTooltipTriangle(color: Color) {
    val path = Path().apply {
        moveTo(size.width / 2 - 8.dp.toPx(), 0f)
        lineTo(size.width / 2 + 8.dp.toPx(), 0f)
        lineTo(size.width / 2, 12.dp.toPx())
        close()
    }
    drawPath(
        path = path,
        color = color,
    )
}

@Composable
private fun BmiCategoryInfo(category: BmiCategory) {
    Row(
        modifier = Modifier
            .padding(horizontal = 12.dp)
            .fillMaxWidth()
            .background(yellow_3, cardShape)
            .padding(vertical = 10.dp, horizontal = 12.dp),
        verticalAlignment = Alignment.Top,
    ) {
        Text(
            text = category.emoji,
            style = AppTheme.textStyles.header,
        )
        Spacer(Modifier.width(8.dp))
        Column(
            modifier = Modifier.weight(1f),
        ) {
            Text(
                text = category.title,
                style = AppTheme.textStyles.captionBold,
                color = AppTheme.colors.text100,
            )
            Text(
                text = category.text,
                style = AppTheme.textStyles.caption,
                color = AppTheme.colors.text100,
            )
        }
    }
}
